/**
 * Helpers to serialize JSON:API parameters consistently across all hooks.
 *
 * Conventions used by the followr-api backend:
 * - filters:  filter[name]=value   (arrays joined with comma; null → "null")
 * - include:  include=user.image,company
 * - sort:     sort=-created_at,title
 * - page:     page[number]=2&page[size]=30
 */

function isEmpty(value) {
    return value === undefined || value === ''
}

function encodeValue(value) {
    if (value === null) return 'null'
    if (Array.isArray(value)) return value.map(String).join(',')
    if (typeof value === 'boolean') return value ? 'true' : 'false'
    return value
}

function appendParam(usp, key, value) {
    if (isEmpty(value)) return
    if (Array.isArray(value) && value.length === 0) return
    usp.append(key, String(encodeValue(value)))
}

export function serializeFilters(filters = {}) {
    const usp = new URLSearchParams()
    Object.entries(filters).forEach(([k, v]) =>
        appendParam(usp, `filter[${k}]`, v)
    )
    return usp
}

export function serializeIncludes(includes) {
    if (!includes || includes.length === 0) return null
    return Array.isArray(includes) ? includes.join(',') : String(includes)
}

export function serializeSort(sort) {
    if (!sort) return null
    return Array.isArray(sort) ? sort.join(',') : String(sort)
}

export function serializePage(page) {
    if (!page) return null
    const out = {}
    if (page.number != null) out['page[number]'] = String(page.number)
    if (page.size != null) out['page[size]'] = String(page.size)
    return out
}

/**
 * Build a URLSearchParams object from a structured options object.
 * Result is alphabetically sorted so identical inputs produce identical querystrings
 * (important for React Query cache-key stability when keys are derived from strings).
 *
 * @param {import('./types').ListParams} [opts]
 */
export function buildSearchParams(opts = {}) {
    const { filter, include, sort, page, extra } = opts
    const parts = serializeFilters(filter || {})

    const inc = serializeIncludes(include)
    if (inc) parts.append('include', inc)

    const srt = serializeSort(sort)
    if (srt) parts.append('sort', srt)

    const pg = serializePage(page)
    if (pg) Object.entries(pg).forEach(([k, v]) => parts.append(k, v))

    if (extra) {
        Object.entries(extra).forEach(([k, v]) => appendParam(parts, k, v))
    }

    parts.sort()
    return parts
}

export function buildQueryString(opts) {
    return buildSearchParams(opts).toString()
}

/**
 * Plain object form for `axios.request({ params: ... })`. Preferred for hooks
 * because React Query hashes object query keys deeply.
 *
 * Output keys: filter[x], include, sort, page[number], page[size], + extras.
 *
 * @param {import('./types').ListParams} [opts]
 */
export function buildJsonApiParams(opts = {}) {
    const { filter, include, sort, page, extra } = opts
    const out = {}

    if (filter) {
        Object.keys(filter)
            .sort()
            .forEach((k) => {
                const v = filter[k]
                if (isEmpty(v)) return
                if (Array.isArray(v) && v.length === 0) return
                out[`filter[${k}]`] = encodeValue(v)
            })
    }

    const inc = serializeIncludes(include)
    if (inc) out.include = inc

    const srt = serializeSort(sort)
    if (srt) out.sort = srt

    if (page?.number != null) out['page[number]'] = page.number
    if (page?.size != null) out['page[size]'] = page.size

    if (extra) {
        Object.keys(extra)
            .sort()
            .forEach((k) => {
                const v = extra[k]
                if (isEmpty(v)) return
                if (Array.isArray(v) && v.length === 0) return
                out[k] = encodeValue(v)
            })
    }

    return out
}

export function apiPath(base, opts) {
    const qs = buildQueryString(opts)
    return qs ? `${base}?${qs}` : base
}
